import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import type { PaginatedPromoRedemptions, PromoListItem } from '../services/types';
import { formatDate } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

export function PromosPage() {
  const { user } = useAuth();
  const canWrite = user?.role === 'admin' || user?.role === 'superadmin';

  const [promos, setPromos] = useState<PromoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [bonusDays, setBonusDays] = useState('30');
  const [tier, setTier] = useState('premium');
  const [maxUses, setMaxUses] = useState('');

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<PaginatedPromoRedemptions | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listPromos();
    if (res.data) setPromos(res.data);
    else if (res.error) setError(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitPromo = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    const res = await api.upsertPromo({
      code: code.trim(),
      bonus_days: Number(bonusDays),
      subscription_tier: tier,
      max_uses: maxUses ? Number(maxUses) : undefined,
      active: true,
    });
    if (res.data) {
      setMessage(`Промокод ${res.data.code} сохранён`);
      setCode('');
      load();
    } else if (res.error) {
      setError(res.error.message);
    }
  };

  const toggleActive = async (promo: PromoListItem) => {
    const res = await api.setPromoActive(promo.code, !promo.active);
    if (res.data) load();
    else if (res.error) setError(res.error.message);
  };

  const showRedemptions = async (promoCode: string) => {
    setSelectedCode(promoCode);
    const res = await api.getPromoRedemptions(promoCode);
    if (res.data) setRedemptions(res.data);
  };

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Промокоды</h1>

      {canWrite && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Создать / обновить промокод</h3>
          <form onSubmit={submitPromo} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              className="input"
              placeholder="CODE"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              style={{ width: 140 }}
              required
            />
            <input
              className="input"
              placeholder="Bonus days"
              value={bonusDays}
              onChange={(e) => setBonusDays(e.target.value)}
              style={{ width: 100 }}
              required
            />
            <select className="select" value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="premium">Premium</option>
              <option value="default">Default</option>
            </select>
            <input
              className="input"
              placeholder="Max uses (opt.)"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              style={{ width: 120 }}
            />
            <button type="submit" className="btn btn-primary">
              Сохранить
            </button>
          </form>
        </div>
      )}

      {message && <p style={{ color: 'var(--success)' }}>{message}</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Код</th>
                <th>Дней</th>
                <th>Tier</th>
                <th>Max uses</th>
                <th>Использовано</th>
                <th>Активен</th>
                <th>Обновлён</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {promos.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ color: 'var(--text-muted)' }}>
                    Промокодов нет
                  </td>
                </tr>
              )}
              {promos.map((p) => (
                <tr key={p.code}>
                  <td>
                    <code>{p.code}</code>
                  </td>
                  <td>{p.bonus_days}</td>
                  <td>{p.subscription_tier}</td>
                  <td>{p.max_uses ?? '∞'}</td>
                  <td>{p.uses_count}</td>
                  <td>
                    <span className={p.active ? 'badge badge-success' : 'badge badge-muted'}>
                      {p.active ? 'да' : 'нет'}
                    </span>
                  </td>
                  <td>{formatDate(p.updated_at)}</td>
                  <td style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" className="btn btn-ghost" onClick={() => showRedemptions(p.code)}>
                      Использования
                    </button>
                    {canWrite && (
                      <button type="button" className="btn btn-ghost" onClick={() => toggleActive(p)}>
                        {p.active ? 'Выкл' : 'Вкл'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCode && redemptions && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Использования: {selectedCode}</h3>
            <button type="button" className="btn btn-ghost" onClick={() => setSelectedCode(null)}>
              Закрыть
            </button>
          </div>
          {redemptions.items.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Ещё не использовался</p>
          ) : (
            <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Дата</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to={`/users/${r.user_id}`}>
                          {r.first_name || r.username || r.user_id}
                        </Link>
                      </td>
                      <td>{formatDate(r.used_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
